/**
 * GameFilterStrip.tsx - Horizontal Game Filter Bar
 * 
 * V6 UX FIX: Replaces dropdown with always-visible horizontal scroll
 * 
 * Benefits:
 * - Zero-tap filtering (no dropdown to open)
 * - Games always visible
 * - Touch-friendly pill buttons
 * - Mobile-optimized scroll behavior
 */

import React from 'react';

interface GameOption {
  id: string;
  label: string;
  home?: string;
  away?: string;
  time?: string;
}

interface GameFilterStripProps {
  games: GameOption[];
  selectedGame: string | null;
  onSelect: (gameId: string | null) => void;
}

export const GameFilterStrip: React.FC<GameFilterStripProps> = ({
  games,
  selectedGame,
  onSelect,
}) => {
  // Add "All Games" option at start
  const allGamesOption: GameOption = { id: 'all', label: 'All Games' };
  const options = [allGamesOption, ...games];

  return (
    <div className="flex gap-2 overflow-x-auto px-4 pb-2 scrollbar-hide">
      {options.map(game => {
        const isActive = game.id === 'all' 
          ? selectedGame === null 
          : selectedGame === game.id;
        
        return (
          <button
            key={game.id}
            onClick={() => onSelect(game.id === 'all' ? null : game.id)}
            className={`
              whitespace-nowrap px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wide 
              transition-all min-h-[44px] flex items-center justify-center
              ${isActive 
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/25 border border-indigo-500' 
                : 'bg-slate-800 text-slate-400 border border-slate-700 hover:bg-slate-750 hover:text-slate-300'}
            `}
          >
            {game.label}
          </button>
        );
      })}
      
      {/* Hide scrollbar but keep functionality */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default GameFilterStrip;
